import React from 'react';
import CollapsibleList from 'components/CollapsibleList/CollapsibleList';
import CollapsibleSection from 'components/CollapsibleList/CollapsibleSection';
import { LayoutDashboard, Settings } from 'lucide-react';
import CollapsibleItem from 'components/CollapsibleList/CollapsibleItem';
import PropTypes from 'prop-types';
import { getCompany } from 'helpers/getCompanyVariables';
import Button from 'tailwind-components/Button/Button';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { areOnboardingInvitesDisabled } from 'helpers/viewing-rules';
import { ONEILL_MAIN_AGENCY_ID } from 'constants/enum';
import { makeAgencyNodeId } from 'helpers/agencyManagement/agency-tree-search';
import { isSingleAgencyWithoutDownline } from 'helpers/agencyManagement/agency-tree';
import {
  agencyRowListProps,
  agencyTitleAnchorProps
} from 'tailwind-components/AgencyTreeFindBar/agencyTreeFindDom';

const company = getCompany();

export const AGENCY_TREE_NODE_ID_PREFIX = 'am';

function buildAgencyOptions(agencyId, invitesDisabled) {
  return [
    {
      text: 'Dashboard',
      icon: <LayoutDashboard size={20} />,
      to: `/agencies/${agencyId}`
    },
    {
      text: invitesDisabled ? 'Settings' : 'Settings/Invites',
      icon: <Settings size={20} />,
      to: `/agencies/${agencyId}/settings`
    }
  ];
}

function renderChilds(Member_Agencies, invitesDisabled, idPath = 'r') {
  if (!Member_Agencies || Member_Agencies.length === 0) return null;

  return Member_Agencies.map((agency, index) => {
    const nextPath = `${idPath}p${index}`;
    const nodeId = makeAgencyNodeId(
      AGENCY_TREE_NODE_ID_PREFIX,
      nextPath,
      agency.AgencyID
    );
    const hasChildren =
      Array.isArray(agency.Member_Agencies) &&
      agency.Member_Agencies.length > 0;

    if (hasChildren) {
      return (
        <CollapsibleSection
          key={nodeId}
          id={nodeId}
          title={agency.AgencyName}
          highlightFirst={agency.AgencyID === ONEILL_MAIN_AGENCY_ID}
          titleColor='secondary'
          expanded
          uncontrolled
          titleAnchorProps={agencyTitleAnchorProps(agency.AgencyID)}
          listItemProps={agencyRowListProps(agency.AgencyID)}
          optionsArray={buildAgencyOptions(agency.AgencyID, invitesDisabled)}
        >
          {renderChilds(agency.Member_Agencies, invitesDisabled, nextPath)}
        </CollapsibleSection>
      );
    }

    return (
      <CollapsibleItem
        key={nodeId}
        title={agency.AgencyName}
        titleAnchorProps={agencyTitleAnchorProps(agency.AgencyID)}
        listItemProps={agencyRowListProps(agency.AgencyID)}
        optionsArray={buildAgencyOptions(agency.AgencyID, invitesDisabled)}
      />
    );
  });
}

/**
 * renders the agencies tree, or the shortcuts for a user with a single agency
 * @param data - the agencies tree from redux
 */
export default function CollapsibleListComponent({ data }) {
  const userAgencyData = useSelector(state => state.user.zohoData.agencyData);
  const invitesDisabled = areOnboardingInvitesDisabled(userAgencyData);

  if (isSingleAgencyWithoutDownline(data)) {
    const agency = data[0];

    return (
      <div
        style={{ marginTop: '16px' }}
        {...agencyRowListProps(agency.AgencyID)}
      >
        <div style={{ marginBottom: '18px' }}>
          <h3
            style={{
              margin: '0 0 8px 0',
              fontSize: '20px',
              fontWeight: 'bold'
            }}
            {...agencyTitleAnchorProps(agency.AgencyID)}
          >
            {agency.AgencyName}
          </h3>
          <p style={{ margin: 0, fontSize: '16px' }}>
            Manage your agency dashboard and settings
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Link
            to={
              company === 'oneill'
                ? `/agency/dashboard/${agency.AgencyID}`
                : `/agencies/${agency.AgencyID}`
            }
            replace
          >
            <Button
              variant='primaryLight'
              size='medium'
              rounded='large'
              text='Go to Dashboard'
              icon={<LayoutDashboard size={20} />}
            />
          </Link>
          <Link
            to={
              company === 'oneill'
                ? `/agency/settings/${agency.AgencyID}`
                : `/agencies/${agency.AgencyID}/settings`
            }
            replace
          >
            <Button
              variant='primaryLight'
              size='medium'
              rounded='large'
              text={
                invitesDisabled ? 'Go to Settings' : 'Go to Settings & Invites'
              }
              icon={<Settings size={20} />}
            />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <CollapsibleList>
      {data.length > 0 && renderChilds(data, invitesDisabled, 'r')}
    </CollapsibleList>
  );
}

CollapsibleListComponent.propTypes = {
  data: PropTypes.array
};
